import React, { useState, useEffect, FormEvent, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { areasAPI, dashboardAPI, logsAPI, type DashboardSummary } from '../../utils/api';
import { getEventTitle } from '../../utils/modelLabels';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import DetectionsStatsModal from '../../components/DetectionsStatsModal/DetectionsStatsModal';
import DetectionPhotoModal from '../../components/DetectionPhotoModal/DetectionPhotoModal';
import AddCameraModal from '../../components/AddCameraModal/AddCameraModal';
import { prefetchAuthenticatedImage } from '../../utils/useAuthenticatedBlobUrl';
import Loader from '../../components/Loader/Loader';
import { SkeletonCard } from '../../components/Skeleton/Skeleton';
import '../../components/Skeleton/Skeleton.css';
import './Dashboard.css';

interface Area {
  id: number;
  name: string;
  description: string | null;
  cameras_count?: number;
  cameras_online?: number;
  cameras_offline?: number;
}

function formatCamerasCount(count: number): string {
  const n = Math.abs(count);
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n} камера`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} камеры`;
  return `${n} камер`;
}

interface RecentEvent {
  id: number;
  timestamp: string | null;
  model: string;
  area_id: number;
  area_name: string;
  camera_id: number;
  camera_name: string;
  screen_url?: string | null;
}

const VIEWED_LOGS_KEY = 'avivion:viewed-log-ids';
const RECENT_EVENTS_PAGE_SIZE = 3;
const DASHBOARD_EVENTS_POLL_MS = 15_000;

function formatEventsChange(percent: number): string {
  if (percent > 0) {
    return `+ ${percent}% к вчера`;
  }
  if (percent < 0) {
    return `${percent}% к вчера`;
  }
  return 'без изменений к вчера';
}

function getViewedLogIds(): Set<number> {
  try {
    const raw = localStorage.getItem(VIEWED_LOGS_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as number[];
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
}

function markLogViewed(id: number): void {
  const viewed = getViewedLogIds();
  viewed.add(id);
  localStorage.setItem(VIEWED_LOGS_KEY, JSON.stringify(Array.from(viewed)));
}

function formatEventTime(timestamp: string | null): string {
  if (!timestamp) return '[ — ]';
  const d = new Date(timestamp.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return '[ — ]';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `[ ${hh}:${mm}:${ss} ]`;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const { requireSubscription } = useSubscription();
  const [areas, setAreas] = useState<Area[]>([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [recentEvents, setRecentEvents] = useState<RecentEvent[]>([]);
  const [recentEventsLoading, setRecentEventsLoading] = useState(true);
  const [recentEventsHasMore, setRecentEventsHasMore] = useState(false);
  const [recentEventsLoadingMore, setRecentEventsLoadingMore] = useState(false);
  const [viewedLogIds, setViewedLogIds] = useState<Set<number>>(() => getViewedLogIds());
  const [isAllAreasStatsOpen, setIsAllAreasStatsOpen] = useState(false);
  const [isDetectionModalOpen, setIsDetectionModalOpen] = useState(false);
  const [detectionPhotoUrl, setDetectionPhotoUrl] = useState<string | null>(null);
  const [detectionModalLoading, setDetectionModalLoading] = useState(false);
  const [detectionModalError, setDetectionModalError] = useState<string | null>(null);
  const [isAddCameraModalOpen, setIsAddCameraModalOpen] = useState(false);
  const [openAreaMenuId, setOpenAreaMenuId] = useState<number | null>(null);
  const areaMenuRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string>('');
  
  // Форма создания
  const [areaName, setAreaName] = useState('');
  const [areaDescription, setAreaDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Модальное окно редактирования
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingArea, setEditingArea] = useState<Area | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  
  // Модальное окно удаления
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingArea, setDeletingArea] = useState<Area | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const recentEventsLimitRef = useRef(RECENT_EVENTS_PAGE_SIZE);

  // Загрузка участков и сводки
  useEffect(() => {
    loadAreas();
    void loadSummary();
    void loadRecentEvents({ sync: true });
  }, []);

  useEffect(() => {
    if (openAreaMenuId == null) return;

    const handleOutside = (e: MouseEvent) => {
      if (areaMenuRef.current && !areaMenuRef.current.contains(e.target as Node)) {
        setOpenAreaMenuId(null);
      }
    };

    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [openAreaMenuId]);

  useEffect(() => {
    if (!isDeleteModalOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isDeleting) {
        setIsDeleteModalOpen(false);
        setDeletingArea(null);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isDeleteModalOpen, isDeleting]);

  const loadSummary = useCallback(async (options?: { silent?: boolean }) => {
    try {
      if (!options?.silent) {
        setSummaryLoading(true);
      }
      const response = await dashboardAPI.getSummary();
      if (response.success) {
        setSummary({
          cameras: response.cameras,
          events: response.events,
        });
      }
    } catch {
      if (!options?.silent) {
        setSummary(null);
      }
    } finally {
      if (!options?.silent) {
        setSummaryLoading(false);
      }
    }
  }, []);

  const loadRecentEvents = useCallback(async (options?: { silent?: boolean; sync?: boolean }) => {
    try {
      if (!options?.silent) {
        setRecentEventsLoading(true);
      }
      const limit = Math.max(RECENT_EVENTS_PAGE_SIZE, recentEventsLimitRef.current);
      const response = await dashboardAPI.getRecentEvents({
        limit,
        offset: 0,
        sync: options?.sync,
      });
      if (response.success) {
        setRecentEvents(response.events);
        setRecentEventsHasMore(response.has_more);
        recentEventsLimitRef.current = Math.max(recentEventsLimitRef.current, response.events.length);
        response.events.forEach((event) => {
          void prefetchAuthenticatedImage(event.screen_url);
        });
      }
    } catch {
      if (!options?.silent) {
        setRecentEvents([]);
        setRecentEventsHasMore(false);
      }
    } finally {
      if (!options?.silent) {
        setRecentEventsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const refreshFeed = () => {
      void loadRecentEvents({ silent: true, sync: true });
      void loadSummary({ silent: true });
    };

    const intervalId = window.setInterval(refreshFeed, DASHBOARD_EVENTS_POLL_MS);

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        refreshFeed();
      }
    };

    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [loadRecentEvents, loadSummary]);

  const loadMoreRecentEvents = async () => {
    try {
      setRecentEventsLoadingMore(true);
      const response = await dashboardAPI.getRecentEvents({
        limit: RECENT_EVENTS_PAGE_SIZE,
        offset: recentEvents.length,
      });
      if (response.success) {
        setRecentEvents((prev) => {
          const next = [...prev, ...response.events];
          recentEventsLimitRef.current = next.length;
          return next;
        });
        setRecentEventsHasMore(response.has_more);
        response.events.forEach((event) => {
          void prefetchAuthenticatedImage(event.screen_url);
        });
      }
    } finally {
      setRecentEventsLoadingMore(false);
    }
  };

  const openDetectionModal = async (event: RecentEvent) => {
    setDetectionModalError(null);
    setIsDetectionModalOpen(true);

    if (event.screen_url) {
      setDetectionPhotoUrl(event.screen_url);
      setDetectionModalLoading(false);
      void prefetchAuthenticatedImage(event.screen_url);
      return;
    }

    setDetectionPhotoUrl(null);
    setDetectionModalLoading(true);

    try {
      const response = await logsAPI.getById(event.area_id, event.id);
      if (response.success && response.log?.screen_url) {
        setDetectionPhotoUrl(response.log.screen_url);
        void prefetchAuthenticatedImage(response.log.screen_url);
      } else {
        setDetectionModalError('Фотофиксация для этого события недоступна');
      }
    } catch {
      setDetectionModalError('Не удалось загрузить событие');
    } finally {
      setDetectionModalLoading(false);
    }
  };

  const closeDetectionModal = () => {
    setIsDetectionModalOpen(false);
    setDetectionPhotoUrl(null);
    setDetectionModalError(null);
    setDetectionModalLoading(false);
  };

  const handleOpenEvent = useCallback((event: RecentEvent) => {
    markLogViewed(event.id);
    setViewedLogIds(getViewedLogIds());
    requireSubscription(() => {
      void openDetectionModal(event);
    });
  }, [requireSubscription]);

  const openAddCameraModal = () => {
    requireSubscription(() => setIsAddCameraModalOpen(true));
  };

  const openFirstArea = useCallback(() => {
    if (areas.length === 0) {
      setError('Сначала создайте участок');
      return;
    }
    requireSubscription(() => {
      navigate(`/area/${areas[0].id}`);
    });
  }, [areas, navigate, requireSubscription]);

  const openReportsModal = () => {
    requireSubscription(() => setIsAllAreasStatsOpen(true));
  };

  const handleAllEvents = () => {
    const areaId = recentEvents[0]?.area_id ?? areas[0]?.id;
    if (!areaId) {
      setError('Сначала создайте участок');
      return;
    }
    requireSubscription(() => navigate(`/area/${areaId}`));
  };

  const loadAreas = async () => {
    try {
      setLoading(true);
      const response = await areasAPI.getAll();
      if (response.success) {
        setAreas(response.areas);
      }
    } catch (err: any) {
      setError(err.message || 'Ошибка загрузки участков');
    } finally {
      setLoading(false);
    }
  };

  // Создание участка
  const handleCreateArea = async (e: FormEvent) => {
    e.preventDefault();
    if (!areaName.trim()) {
      setError('Введите название участка');
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');
      const response = await areasAPI.create(areaName.trim(), areaDescription.trim() || undefined);
      if (response.success) {
        setAreaName('');
        setAreaDescription('');
        await loadAreas();
      }
    } catch (err: any) {
      setError(err.message || 'Ошибка создания участка');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Открытие модального окна редактирования
  const handleEditClick = (area: Area) => {
    setEditingArea(area);
    setEditName(area.name);
    setEditDescription(area.description || '');
    setIsEditModalOpen(true);
  };

  // Сохранение изменений
  const handleUpdateArea = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingArea || !editName.trim()) {
      setError('Введите название участка');
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');
      const response = await areasAPI.update(
        editingArea.id,
        editName.trim(),
        editDescription.trim() || undefined
      );
      if (response.success) {
        setIsEditModalOpen(false);
        setEditingArea(null);
        await loadAreas();
      }
    } catch (err: any) {
      setError(err.message || 'Ошибка обновления участка');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Открытие модального окна удаления
  const handleDeleteClick = (area: Area) => {
    requireSubscription(() => {
      setError('');
      setDeletingArea(area);
      setIsDeleteModalOpen(true);
    });
  };

  // Подтверждение удаления участка
  const handleConfirmDelete = async () => {
    if (!deletingArea) return;

    try {
      setIsDeleting(true);
      setError('');
      const response = await areasAPI.delete(deletingArea.id);
      if (response.success) {
        setIsDeleteModalOpen(false);
        setDeletingArea(null);
        await loadAreas();
      }
    } catch (err: any) {
      setError(err.message || 'Ошибка удаления участка');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="dashboard">
      <Header />
      <main className="dashboard-main container">
        <section className="dashboard-welcome">
          <h1 className="dashboard-welcome__title">
            <span className="dashboard-welcome-title-span">Добро пожаловать,</span>
            <br />
            {user ? (
              user.name
            ) : (
              <span className="dashboard-welcome__name-skeleton">
                <div className="skeleton" style={{ width: '150px', height: '50px', borderRadius: '8px' }}></div>
              </span>
            )}
          </h1>
          <p className="dashboard-welcome__text">
            Создайте участок, добавляйте камеры, выбирайте необходимые аналитики,
            отслеживайте результаты
          </p>

          <div className="dashboard-stats">
            <article className="dashboard-stat-card">
              <div className="dashboard-stat-card__icon-wrap" aria-hidden="true">
                <img
                  src={`${process.env.PUBLIC_URL}/icons/svg_icon/camera.svg`}
                  alt=""
                  className="dashboard-stat-card__icon"
                />
              </div>
              <div className="dashboard-stat-card__body">
                <span className="dashboard-stat-card__label">Камер онлайн</span>
                {summaryLoading ? (
                  <div className="dashboard-stat-card__value-skeleton skeleton" />
                ) : (
                  <strong className="dashboard-stat-card__value">
                    {summary ? `${summary.cameras.online}/${summary.cameras.total}` : '—'}
                  </strong>
                )}
                <span className="dashboard-stat-card__hint">
                  {summaryLoading
                    ? 'Загрузка…'
                    : summary && summary.cameras.total > 0
                      ? `${summary.cameras.online_percent}% от всех камер`
                      : 'Нет камер'}
                </span>
              </div>
            </article>

            <article className="dashboard-stat-card">
              <div className="dashboard-stat-card__icon-wrap" aria-hidden="true">
                <svg className="dashboard-stat-card__icon dashboard-stat-card__icon--chart" viewBox="0 0 24 24" fill="none">
                  <path d="M4 19V5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path d="M4 19H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path d="M8 15V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path d="M12 15V8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path d="M16 15V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </div>
              <div className="dashboard-stat-card__body">
                <span className="dashboard-stat-card__label">События за сегодня</span>
                {summaryLoading ? (
                  <div className="dashboard-stat-card__value-skeleton skeleton" />
                ) : (
                  <strong className="dashboard-stat-card__value">
                    {summary ? summary.events.today : '—'}
                  </strong>
                )}
                <span className="dashboard-stat-card__hint">
                  {summaryLoading
                    ? 'Загрузка…'
                    : summary
                      ? formatEventsChange(summary.events.change_percent)
                      : '—'}
                </span>
              </div>
            </article>
          </div>
        </section>

        <section className="dashboard-card">
          <h2 className="dashboard-card__title">Создать участок группы камер</h2>

          {error && (
            <div style={{ color: '#ff4444', padding: '10px', marginBottom: '10px', fontSize: '14px' }}>
              {error}
            </div>
          )}

          <form className="dashboard-card__form" onSubmit={handleCreateArea}>
            <div className="dashboard-card__field">
              <input
                type="text"
                className="dashboard-input"
                placeholder="Название участка"
                value={areaName}
                onChange={(e) => setAreaName(e.target.value)}
                required
              />
            </div>

            <div className="dashboard-card__field">
              <input
                type="text"
                className="dashboard-input"
                placeholder="Описание"
                value={areaDescription}
                onChange={(e) => setAreaDescription(e.target.value)}
              />
            </div>

            <button type="submit" className="dashboard-button" disabled={isSubmitting}>
              <span className="dashboard-button__icon">+</span>
              <span>{isSubmitting ? 'Создание...' : 'Добавить участок'}</span>
            </button>
          </form>
        </section>
      </main>

      <section className="dashboard-panels container">
        <article className="dashboard-panel dashboard-panel--events">
          <header className="dashboard-panel__header">
            <h2 className="dashboard-panel__title">Последние события</h2>
            <button type="button" className="dashboard-panel__link" onClick={handleAllEvents}>
              Все события
            </button>
          </header>

          {recentEventsLoading ? (
            <div className="dashboard-panel__loading">Загрузка событий…</div>
          ) : recentEvents.length === 0 ? (
            <div className="dashboard-panel__empty">Событий пока нет</div>
          ) : (
            <ul className="dashboard-events-list">
              {recentEvents.map((event) => {
                const isViewed = viewedLogIds.has(event.id);
                return (
                  <li key={event.id}>
                    <button
                      type="button"
                      className="dashboard-event-row"
                      onClick={() => handleOpenEvent(event)}
                      onMouseEnter={() => void prefetchAuthenticatedImage(event.screen_url)}
                    >
                      <span className="dashboard-event-row__icon-wrap" aria-hidden="true">
                        <img
                          src={`${process.env.PUBLIC_URL}/icons/modals/people.svg`}
                          alt=""
                          className="dashboard-event-row__icon"
                        />
                      </span>
                      <span className="dashboard-event-row__time">{formatEventTime(event.timestamp)}</span>
                      <span className="dashboard-event-row__content">
                        <span className="dashboard-event-row__title">{getEventTitle(event.model)}</span>
                        <span className="dashboard-event-row__meta">
                          {event.area_name} • {event.camera_name}
                        </span>
                      </span>
                      <span
                        className={`dashboard-event-row__badge ${
                          isViewed ? 'dashboard-event-row__badge--viewed' : 'dashboard-event-row__badge--new'
                        }`}
                      >
                        {isViewed ? 'Просмотрено' : 'Новое'}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {recentEventsHasMore && !recentEventsLoading && (
            <button
              type="button"
              className="dashboard-panel__more"
              onClick={loadMoreRecentEvents}
              disabled={recentEventsLoadingMore}
            >
              {recentEventsLoadingMore ? 'Загрузка…' : (
                <>
                  Показать еще
                  <svg
                    className="dashboard-panel__more-chevron"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M6 9L12 15L18 9"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </>
              )}
            </button>
          )}
        </article>

        <article className="dashboard-panel dashboard-panel--actions">
          <h2 className="dashboard-panel__title">Быстрые действия</h2>
          <ul className="dashboard-actions-list">
            <li>
              <button type="button" className="dashboard-action-row" onClick={openAddCameraModal}>
                <span className="dashboard-action-row__icon-wrap" aria-hidden="true">
                  <img src={`${process.env.PUBLIC_URL}/icons/svg_icon/camera.svg`} alt="" />
                </span>
                <span className="dashboard-action-row__text">
                  <span className="dashboard-action-row__title">Добавить камеру</span>
                  <span className="dashboard-action-row__desc">Подключить камеру к системе</span>
                </span>
                <span className="dashboard-action-row__chevron" aria-hidden="true">›</span>
              </button>
            </li>
            <li>
              <button type="button" className="dashboard-action-row" onClick={() => openFirstArea()}>
                <span className="dashboard-action-row__icon-wrap" aria-hidden="true">
                  <img src={`${process.env.PUBLIC_URL}/icons/svg_icon/roi.svg`} alt="" />
                </span>
                <span className="dashboard-action-row__text">
                  <span className="dashboard-action-row__title">Добавить аналитику</span>
                  <span className="dashboard-action-row__desc">Настроить детекцию</span>
                </span>
                <span className="dashboard-action-row__chevron" aria-hidden="true">›</span>
              </button>
            </li>
            <li>
              <button
                type="button"
                className="dashboard-action-row"
                onClick={openReportsModal}
              >
                <span className="dashboard-action-row__icon-wrap" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" className="dashboard-action-row__icon-svg">
                    <path d="M4 19V5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <path d="M4 19H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <path d="M8 15V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <path d="M12 15V8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <path d="M16 15V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </span>
                <span className="dashboard-action-row__text">
                  <span className="dashboard-action-row__title">Посмотреть отчеты</span>
                  <span className="dashboard-action-row__desc">Аналитика и статистика</span>
                </span>
                <span className="dashboard-action-row__chevron" aria-hidden="true">›</span>
              </button>
            </li>
            <li>
              <button
                type="button"
                className="dashboard-action-row"
                onClick={() => (isAdmin ? navigate('/admin') : navigate('/profile'))}
              >
                <span className="dashboard-action-row__icon-wrap" aria-hidden="true">
                  <img src={`${process.env.PUBLIC_URL}/icons/header/user.svg`} alt="" />
                </span>
                <span className="dashboard-action-row__text">
                  <span className="dashboard-action-row__title">Управление пользователями</span>
                  <span className="dashboard-action-row__desc">Доступ к системе</span>
                </span>
                <span className="dashboard-action-row__chevron" aria-hidden="true">›</span>
              </button>
            </li>
          </ul>
        </article>
      </section>

      <section className="dashboard-areas">
        <div className="container">
          <h2 className="dashboard-areas__title">Участки с камерами</h2>

          {loading ? (
            <div className="dashboard-areas__grid">
              {Array.from({ length: 4 }).map((_, index) => (
                <SkeletonCard key={index} />
              ))}
            </div>
          ) : areas.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#6c6c70' }}>
              Участки не найдены. Создайте первый участок выше.
            </div>
          ) : (
            <div className="dashboard-areas__grid">
              {areas.map((area) => {
                const total = area.cameras_count ?? 0;
                const online = area.cameras_online ?? 0;
                const offline = area.cameras_offline ?? Math.max(0, total - online);

                return (
                  <article key={area.id} className="area-card">
                    <header className="area-card__header">
                      <h3 className="area-card__title">{area.name}</h3>
                      <div
                        className="area-card__menu-wrap"
                        ref={openAreaMenuId === area.id ? areaMenuRef : null}
                      >
                        <button
                          type="button"
                          className="area-card__menu-btn"
                          aria-label="Действия с участком"
                          aria-expanded={openAreaMenuId === area.id}
                          onClick={() =>
                            setOpenAreaMenuId((prev) => (prev === area.id ? null : area.id))
                          }
                        >
                          <span className="area-card__menu-dots" aria-hidden="true">
                            <span />
                            <span />
                            <span />
                          </span>
                        </button>
                        {openAreaMenuId === area.id && (
                          <div className="area-card__menu" role="menu">
                            <button
                              type="button"
                              className="area-card__menu-item"
                              role="menuitem"
                              onClick={() => {
                                setOpenAreaMenuId(null);
                                handleEditClick(area);
                              }}
                            >
                              Редактировать
                            </button>
                            <button
                              type="button"
                              className="area-card__menu-item area-card__menu-item--danger"
                              role="menuitem"
                              onClick={() => {
                                setOpenAreaMenuId(null);
                                handleDeleteClick(area);
                              }}
                            >
                              Удалить
                            </button>
                          </div>
                        )}
                      </div>
                    </header>

                    <div className="area-card__stats">
                      <p className="area-card__cameras-total">{formatCamerasCount(total)}</p>
                      <p className="area-card__cameras-status">
                        <span className="area-card__status-online">{online} онлайн</span>
                        <span className="area-card__status-sep" aria-hidden="true">•</span>
                        <span className="area-card__status-offline">{offline} оффлайн</span>
                      </p>
                    </div>

                    <button
                      className="area-card__open"
                      type="button"
                      onClick={() => requireSubscription(() => navigate(`/area/${area.id}`))}
                    >
                      Открыть
                    </button>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Модальное окно редактирования */}
      {isEditModalOpen && editingArea && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setIsEditModalOpen(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '24px',
              padding: '32px',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0, marginBottom: '24px', fontSize: '24px', fontWeight: 600 }}>
              Редактировать участок
            </h2>

            {error && (
              <div style={{ color: '#ff4444', padding: '10px', marginBottom: '10px', fontSize: '14px' }}>
                {error}
              </div>
            )}

            <form onSubmit={handleUpdateArea}>
              <div style={{ marginBottom: '16px' }}>
                <input
                  type="text"
                  className="dashboard-input"
                  placeholder="Название участка"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  required
                  style={{ width: '100%' }}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <input
                  type="text"
                  className="dashboard-input"
                  placeholder="Описание"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>

              <button
                type="submit"
                className="dashboard-button"
                disabled={isSubmitting}
                style={{ width: '100%' }}
              >
                {isSubmitting ? 'Сохранение...' : 'Сохранить'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Модальное окно подтверждения удаления */}
      {isDeleteModalOpen && deletingArea && (
        <div
          className="delete-confirm-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-confirm-title"
          onClick={() => !isDeleting && setIsDeleteModalOpen(false)}
        >
          <div className="delete-confirm-modal__dialog" onClick={(e) => e.stopPropagation()}>
            <h2 id="delete-confirm-title" className="delete-confirm-modal__title">
              Подтвердите действие
            </h2>

            <p className="delete-confirm-modal__text">
              Вы уверены, что хотите удалить участок{' '}
              <strong>«{deletingArea.name}»</strong>?
            </p>

            <div className="delete-confirm-modal__alert" role="alert">
              <p className="delete-confirm-modal__alert-title">Внимание</p>
              <p className="delete-confirm-modal__alert-text">
                Все камеры этого участка будут удалены безвозвратно.
              </p>
            </div>

            <p className="delete-confirm-modal__hint">Это действие нельзя отменить.</p>

            {error && <p className="delete-confirm-modal__error">{error}</p>}

            <button
              type="button"
              className="delete-confirm-modal__submit"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Удаление...' : 'Удалить'}
            </button>
          </div>
        </div>
      )}

      {isAddCameraModalOpen && (
        <AddCameraModal
          areas={areas}
          onClose={() => setIsAddCameraModalOpen(false)}
          onSuccess={loadAreas}
        />
      )}

      {isAllAreasStatsOpen && (
        <DetectionsStatsModal
          areaName="Все участки"
          onClose={() => setIsAllAreasStatsOpen(false)}
        />
      )}

      <DetectionPhotoModal
        isOpen={isDetectionModalOpen}
        photoUrl={detectionPhotoUrl}
        onClose={closeDetectionModal}
        fetchLoading={detectionModalLoading}
        fetchError={detectionModalError}
      />

      <Footer />
    </div>
  );
};

export default Dashboard;
